import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Button,
  Row,
  Col,
  Alert,
  Input,
  Label,
  FormGroup,
} from 'reactstrap';
import Loading from '../../components/Loading';
import { rolesService } from '../../services/rolesService';
import { permisosService } from '../../services/permisosService';

function RolForm({ rol, onSuccess, onCancel }) {
  const [formData, setFormData] = useState({
    Nombre: '',
    Descripcion: '',
    Activo: true,
  });
  const [permisosDisponibles, setPermisosDisponibles] = useState([]);
  const [permisosSeleccionados, setPermisosSeleccionados] = useState([]);
  const [permisosAgrupados, setPermisosAgrupados] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadPermisos();
  }, []);

  useEffect(() => {
    if (rol) {
      setFormData({
        Nombre: rol.Nombre || rol.nombre || '',
        Descripcion: rol.Descripcion || rol.descripcion || '',
        Activo: rol.Activo !== undefined ? rol.Activo : (rol.activo !== undefined ? rol.activo : true),
      });
      setPermisosSeleccionados((rol.Permisos || rol.permisos || []).map(p => p.PermisoID || p.permisoID));
    }
  }, [rol]);

  useEffect(() => {
    // Agrupar permisos por módulo
    const agrupados = {};
    permisosDisponibles.forEach(permiso => {
      const modulo = permiso.Modulo || permiso.modulo || 'Otros';
      if (!agrupados[modulo]) {
        agrupados[modulo] = [];
      }
      agrupados[modulo].push(permiso);
    });
    setPermisosAgrupados(agrupados);
  }, [permisosDisponibles]);

  const loadPermisos = async () => {
    try {
      setLoadingData(true);
      const response = await permisosService.getAll();
      if (response.success && response.data) {
        const permisosData = Array.isArray(response.data) ? response.data : [];
        setPermisosDisponibles(permisosData.map(p => ({
          PermisoID: p.PermisoID || p.permisoID,
          Nombre: p.Nombre || p.nombre || '',
          Descripcion: p.Descripcion || p.descripcion,
          Modulo: p.Modulo || p.modulo || '',
          Accion: p.Accion || p.accion || '',
        })));
      }
    } catch (err) {
      setError('Error al cargar permisos');
    } finally {
      setLoadingData(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const togglePermiso = (permisoID) => {
    setPermisosSeleccionados(prev => {
      if (prev.includes(permisoID)) {
        return prev.filter(id => id !== permisoID);
      } else {
        return [...prev, permisoID];
      }
    });
  };

  const toggleModulo = (modulo) => {
    const permisosDelModulo = permisosAgrupados[modulo] || [];
    const todosSeleccionados = permisosDelModulo.every(p => 
      permisosSeleccionados.includes(p.PermisoID)
    );

    if (todosSeleccionados) {
      // Deseleccionar todos los permisos del módulo
      setPermisosSeleccionados(prev => 
        prev.filter(id => !permisosDelModulo.some(p => p.PermisoID === id))
      );
    } else {
      // Seleccionar todos los permisos del módulo
      const nuevosIDs = permisosDelModulo.map(p => p.PermisoID);
      setPermisosSeleccionados(prev => {
        const sinModulo = prev.filter(id => !permisosDelModulo.some(p => p.PermisoID === id));
        return [...sinModulo, ...nuevosIDs];
      });
    }
  };

  const validate = () => {
    if (!formData.Nombre.trim()) {
      setError('El nombre es requerido');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validate()) {
      return;
    }

    try {
      setLoading(true);

      if (rol) {
        // Actualizar rol
        const updateData = {
          Nombre: formData.Nombre.trim(),
          Descripcion: formData.Descripcion.trim() || null,
          Activo: formData.Activo,
        };

        const response = await rolesService.update(rol.RolID || rol.rolID, updateData);

        if (response.success) {
          // Asignar permisos
          await rolesService.asignarPermisos(rol.RolID || rol.rolID, permisosSeleccionados);
          
          onSuccess();
        } else {
          setError(response.Message || response.message || 'Error al actualizar el rol');
        }
      } else {
        // Crear rol
        const createData = {
          Nombre: formData.Nombre.trim(),
          Descripcion: formData.Descripcion.trim() || null,
        };

        const response = await rolesService.create(createData);

        if (response.success) {
          const nuevoRol = response.data;
          const rolId = nuevoRol.RolID || nuevoRol.rolID;

          // Asignar permisos
          if (permisosSeleccionados.length > 0) {
            await rolesService.asignarPermisos(rolId, permisosSeleccionados);
          }
          
          onSuccess();
        } else {
          setError(response.Message || response.message || 'Error al crear el rol');
        }
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return <Loading />;
  }

  const modulos = Object.keys(permisosAgrupados).sort();

  return (
    <div className="content">
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <CardTitle tag="h4">
                {rol ? 'Editar Rol' : 'Nuevo Rol'}
              </CardTitle>
            </CardHeader>
            <CardBody>
              {error && (
                <Alert color="danger" className="mb-3">
                  {error}
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label>Nombre *</Label>
                      <Input
                        type="text"
                        name="Nombre"
                        value={formData.Nombre}
                        onChange={handleChange}
                        required
                      />
                    </FormGroup>
                  </Col>
                  {rol && (
                    <Col md="6">
                      <FormGroup>
                        <Label>Estado</Label>
                        <FormGroup check>
                          <Label check>
                            <Input
                              type="checkbox"
                              name="Activo"
                              checked={formData.Activo}
                              onChange={handleChange}
                            />
                            {' '}Activo
                          </Label>
                        </FormGroup>
                      </FormGroup>
                    </Col>
                  )}
                </Row>

                <Row>
                  <Col md="12">
                    <FormGroup>
                      <Label>Descripción</Label>
                      <Input
                        type="textarea"
                        name="Descripcion"
                        value={formData.Descripcion}
                        onChange={handleChange}
                        rows="3"
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md="12">
                    <Label>Permisos</Label>
                    <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '0.25rem', padding: '0.5rem' }}>
                      {modulos.length === 0 ? (
                        <p className="text-muted">No hay permisos disponibles</p>
                      ) : (
                        modulos.map((modulo) => {
                          const permisosDelModulo = permisosAgrupados[modulo] || [];
                          const todosSeleccionados = permisosDelModulo.every(p => 
                            permisosSeleccionados.includes(p.PermisoID)
                          );
                          const algunosSeleccionados = permisosDelModulo.some(p => 
                            permisosSeleccionados.includes(p.PermisoID)
                          );

                          return (
                            <div key={modulo} className="mb-3" style={{ borderBottom: '1px solid #dee2e6', paddingBottom: '0.5rem' }}>
                              <FormGroup check>
                                <Label check>
                                  <Input
                                    type="checkbox"
                                    checked={todosSeleccionados}
                                    ref={(input) => {
                                      if (input) input.indeterminate = algunosSeleccionados && !todosSeleccionados;
                                    }}
                                    onChange={() => toggleModulo(modulo)}
                                  />
                                  {' '}
                                  <strong>{modulo}</strong>
                                  {' '}
                                  <small className="text-muted">
                                    ({permisosDelModulo.filter(p => permisosSeleccionados.includes(p.PermisoID)).length} / {permisosDelModulo.length})
                                  </small>
                                </Label>
                              </FormGroup>
                              <div style={{ marginLeft: '1.5rem', marginTop: '0.25rem' }}>
                                {permisosDelModulo.map((permiso) => (
                                  <FormGroup check key={permiso.PermisoID}>
                                    <Label check>
                                      <Input
                                        type="checkbox"
                                        checked={permisosSeleccionados.includes(permiso.PermisoID)}
                                        onChange={() => togglePermiso(permiso.PermisoID)}
                                      />
                                      {' '}{permiso.Nombre}
                                      {permiso.Descripcion && (
                                        <small className="text-muted d-block ml-4">
                                          {permiso.Descripcion}
                                        </small>
                                      )}
                                    </Label>
                                  </FormGroup>
                                ))}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                    {permisosSeleccionados.length > 0 && (
                      <div className="mt-2">
                        <small className="text-muted">
                          Permisos seleccionados: {permisosSeleccionados.length}
                        </small>
                      </div>
                    )}
                  </Col>
                </Row>

                <Row>
                  <Col md="12" className="text-right">
                    <Button color="secondary" onClick={onCancel} className="mr-2">
                      Cancelar
                    </Button>
                    <Button color="primary" type="submit" disabled={loading}>
                      {loading ? 'Guardando...' : 'Guardar'}
                    </Button>
                  </Col>
                </Row>
              </form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default RolForm;

